import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/Header";
import AnalysisOutput from "@/components/AnalysisOutput";
import PressKit from "@/components/PressKit";
import { api, downloadAuthed } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { ArrowLeft, Newspaper, Loader2, FileDown, FileText, X, Plus } from "lucide-react";
import { toast } from "sonner";

export default function AnalysisDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pressKit, setPressKit] = useState(null);
  const [pressLoading, setPressLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [tagsSaving, setTagsSaving] = useState(false);
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    api
      .get(`/analyses/${id}`)
      .then((r) => {
        setItem(r.data);
        setTags(r.data?.tags || []);
        if (r.data?.press_kit) setPressKit(r.data.press_kit);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const generatePressKit = async (refresh = false) => {
    setPressLoading(true);
    try {
      const r = await api.post(`/analyses/${id}/press-kit${refresh ? "?refresh=true" : ""}`);
      setPressKit(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setPressLoading(false);
    }
  };

  const addTag = (raw) => {
    const tg = (raw || "").trim().toLowerCase().replace(/^#+/, "");
    if (!tg) return;
    if (tags.includes(tg)) return;
    if (tags.length >= 10) {
      toast.error("Max 10");
      return;
    }
    setTags([...tags, tg]);
  };

  const removeTag = (tg) => setTags(tags.filter((x) => x !== tg));

  const saveTags = async () => {
    setTagsSaving(true);
    try {
      const r = await api.put(`/analyses/${id}/tags`, { tags });
      setTags(r.data.tags || []);
      toast.success("✓");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setTagsSaving(false);
    }
  };

  const exportFile = async (fmt) => {
    setExporting(fmt);
    try {
      const ext = fmt === "pdf" ? "pdf" : "md";
      const safe = (item?.title || "analysis").replace(/[^a-zA-Z0-9]/g, "-").slice(0, 60).replace(/^-+|-+$/g, "") || "analysis";
      await downloadAuthed(`/analyses/${id}/export?format=${fmt}`, `GalascioDAS-${safe}.${ext}`);
      toast.success("✓");
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setExporting("");
    }
  };

  const tagsDirty = item && JSON.stringify(tags) !== JSON.stringify(item.tags || []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="analysis-detail-page">
      <Header />
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-10 md:py-16 space-y-8">
        <Link
          to="/app"
          data-testid="back-link"
          className="font-mono-tech text-xs uppercase tracking-[0.3em] inline-flex items-center gap-2 hover:text-[#D90429]"
        >
          <ArrowLeft className="w-3 h-3" /> {t("detail_back")}
        </Link>
        {loading && (
          <div className="font-mono-tech text-xs uppercase tracking-widest">···</div>
        )}
        {item && (
          <>
            <header className="border-b border-[#171717] pb-6 grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-3">
                  · CASO · {item.id.slice(0, 8).toUpperCase()}
                </div>
                <h1 className="font-serif-display text-3xl md:text-5xl font-bold leading-tight">
                  {item.title}
                </h1>
              </div>
              <div className="space-y-2 font-mono-tech text-[11px] tracking-widest uppercase">
                <div className="flex justify-between border-b border-[#E5E5E5] pb-2">
                  <span className="text-[#A3A3A3]">{t("detail_model")}</span>
                  <span>{item.model}</span>
                </div>
                <div className="flex justify-between border-b border-[#E5E5E5] pb-2">
                  <span className="text-[#A3A3A3]">Lang</span>
                  <span>{item.language.toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#A3A3A3]">{t("detail_date")}</span>
                  <span>{new Date(item.created_at).toLocaleString()}</span>
                </div>
              </div>
            </header>

            {/* Tags editor + Export */}
            <section className="border border-[#171717] bg-white p-6" data-testid="tags-editor">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-3">
                    · {t("detail_tags")}
                  </div>
                  <div className="font-sans-body text-[11px] text-[#737373] leading-snug mb-3" data-testid="tags-hint">
                    {t("tags_hint")}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3 min-h-[28px]" data-testid="tags-chips">
                    {tags.map((tg) => (
                      <span
                        key={tg}
                        className="font-mono-tech text-[10px] uppercase tracking-widest bg-[#0A0A0A] text-white px-2 py-1 inline-flex items-center gap-1"
                      >
                        #{tg}
                        <button
                          data-testid={`tag-remove-${tg}`}
                          onClick={() => removeTag(tg)}
                          className="hover:text-[#D90429]"
                          aria-label={`remove ${tg}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    {tags.length === 0 && (
                      <span className="font-mono-tech text-[10px] uppercase text-[#A3A3A3] tracking-widest">
                        —
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input
                      data-testid="tag-input"
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTag(tagInput);
                          setTagInput("");
                        }
                      }}
                      placeholder={t("detail_tags_add")}
                      className="flex-1 border-b border-[#0A0A0A] bg-transparent px-0 py-2 text-sm focus:outline-none focus:border-[#D90429]"
                    />
                    <button
                      data-testid="tag-add-btn"
                      onClick={() => {
                        addTag(tagInput);
                        setTagInput("");
                      }}
                      className="px-3 py-2 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white font-mono-tech text-xs uppercase tracking-widest inline-flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                    <button
                      data-testid="tag-save-btn"
                      onClick={saveTags}
                      disabled={tagsSaving || !tagsDirty}
                      className="px-3 py-2 border border-[#D90429] bg-[#D90429] text-white font-mono-tech text-xs uppercase tracking-widest disabled:opacity-30"
                    >
                      {tagsSaving ? "···" : t("detail_tags_save")}
                    </button>
                  </div>
                </div>

                <div className="md:border-l md:border-[#E5E5E5] md:pl-6">
                  <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-3">
                    · EXPORT
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      data-testid="export-md-btn"
                      onClick={() => exportFile("md")}
                      disabled={exporting !== ""}
                      className="px-3 py-2 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white font-mono-tech text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-40"
                    >
                      {exporting === "md" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                      {t("detail_export_md")}
                    </button>
                    <button
                      data-testid="export-pdf-btn"
                      onClick={() => exportFile("pdf")}
                      disabled={exporting !== ""}
                      className="px-3 py-2 border border-[#0A0A0A] bg-[#0A0A0A] text-white hover:bg-[#D90429] hover:border-[#D90429] font-mono-tech text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-40"
                    >
                      {exporting === "pdf" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
                      {t("detail_export_pdf")}
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <details className="border border-[#171717] bg-white p-6">
              <summary className="cursor-pointer font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#D90429]">
                {t("detail_input_label")}
              </summary>
              <div className="font-serif-display italic text-lg leading-relaxed text-[#262626] mt-4 whitespace-pre-wrap">
                {item.input_text}
              </div>
            </details>

            <AnalysisOutput text={item.result} streaming={false} />

            <section className="flex flex-wrap items-center justify-between gap-4 border border-[#171717] bg-[#FAFAFA] p-5">
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-1">
                  · KIT DE PRENSA · ESTE ANÁLISIS
                </div>
                <div className="font-serif-display text-xl leading-tight">
                  {t("detail_press_kit_title")}
                </div>
                <div className="font-sans-body text-xs text-[#525252] mt-1">
                  {t("detail_press_kit_lead")}
                </div>
              </div>
              <button
                data-testid="open-press-kit-detail-btn"
                onClick={() => (pressKit ? setPressKit({ ...pressKit }) : generatePressKit(false))}
                disabled={pressLoading}
                className="bg-[#0A0A0A] text-white px-6 py-4 font-mono-tech text-xs uppercase tracking-[0.3em] border border-[#0A0A0A] hover:bg-[#D90429] hover:border-[#D90429] transition-colors disabled:opacity-40 inline-flex items-center gap-3"
              >
                {pressLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Newspaper className="w-4 h-4" />}
                {pressLoading ? t("press_generating") : t("press_mode")} →
              </button>
            </section>
          </>
        )}
      </div>

      {pressKit && (
        <PressKit
          kit={pressKit}
          refreshing={pressLoading}
          onRefresh={() => generatePressKit(true)}
          onClose={() => setPressKit(null)}
        />
      )}
    </div>
  );
}
